"use client";
/* eslint-disable no-param-reassign */
import type { StoreData } from "@components/store-locator/controller";
import { shoeConventionForSite } from "@config/site/site-config";

import { useIsomorphicLayoutEffect } from "@ui/hooks/useIsomorphicLayoutEffect";
import { produce } from "immer";
import { merge } from "lodash-es";
import { createContext, useContext, useRef } from "react";
import type { StoreApi } from "zustand";
import { createStore, useStore as useZustandStore } from "zustand";
import { persist } from "zustand/middleware";
import { updateProfile } from "./helpers/profile";
import { initialState } from "./initialState";
import type { Customer, pagePathData, StoreDataType, StoreState, Tracking } from "./types";
import type { ProductVariant } from "@lib/product/types";

export const initializeStore = (preloadedState = {}) => {
    return createStore<StoreState>()(
        persist(
            (set, get) => {
                // Wrapper around Immer "set" to for added type safety
                const setState = (fn: (state: StoreState) => void) => set(produce<StoreState>(fn));

                return {
                    data: merge({}, initialState, preloadedState),
                    setLocked: (payload) =>
                        setState((state) => {
                            state.data.isLocked = payload;
                        }),
                    setActiveData: (cartAndWishlist) =>
                        setState((state) => {
                            state.data.me.cartInfo.cart = cartAndWishlist.cart;
                            state.data.me.wishlistInfo.wishlist = cartAndWishlist.wishlist;
                        }),
                    setIsLoggedIn: (payload) =>
                        setState((state) => {
                            state.data.me.isLoggedIn = payload;
                        }),
                    clearMe: () =>
                        setState((state) => {
                            state.data.me = initialState.me;
                        }),
                    setCart: (cart) =>
                        setState((state) => {
                            state.data.me.cartInfo.cart = cart;
                        }),
                    clearCart: () =>
                        setState((state) => {
                            state.data.me.cartInfo.cart = null;
                        }),
                    setWishlist: (wishlist) =>
                        setState((state) => {
                            state.data.me.wishlistInfo.wishlist = wishlist;
                        }),
                    getWishlist: () => get().data.me.wishlistInfo.wishlist,
                    setGlobalShoeSizeConvention: (globalShoeSizeConvention) =>
                        setState((state) => {
                            state.data.me.globalShoeSizeConvention = globalShoeSizeConvention;
                        }),
                    clearWishlist: () =>
                        setState((state) => {
                            state.data.me.wishlistInfo.wishlist = null;
                            state.data.me.wishlistInfo.masterProducts = null;
                        }),
                    setWishlistIsExecuted: (isExecuted) =>
                        setState((state) => {
                            state.data.me.wishlistInfo.isExecuted = isExecuted;
                        }),
                    setAddToCartIsExecuted: (isExecuted) =>
                        setState((state) => {
                            state.data.me.cartInfo.isExecuted = isExecuted;
                        }),
                    setItemsLimitReached: (payload) =>
                        setState((state) => {
                            state.data.me.cartInfo.itemsLimitReached = payload;
                        }),
                    setMasterProducts: (masterProducts) =>
                        setState((state) => {
                            state.data.me.cartInfo.masterProducts = masterProducts;
                        }),
                    setWishlistMasterProducts: (masterProducts) =>
                        setState((state) => {
                            state.data.me.wishlistInfo.masterProducts = masterProducts;
                        }),
                    setResultsCount: (payload) =>
                        setState((state) => {
                            state.data.products.resultsCount = payload;
                        }),
                    setLastLoadedPage: (payload) =>
                        setState((state) => {
                            state.data.products.lastLoadedPage = payload;
                        }),
                    setLastLoadedPlpContainerHeight: (payload) =>
                        setState((state) => {
                            state.data.products.lastVisitedPlpContainerHeight = payload;
                        }),
                    setInitialFilters: (payload: { color: any; size: any; category: any }) =>
                        setState((state) => {
                            state.data.products.initialFilters = payload;
                        }),
                    setLastVisitedPlp: (payload: string) =>
                        setState((state) => {
                            state.data.products.lastVisitedPlp = payload;
                        }),
                    setDraftPromoCode: (draftPromoCode: string) =>
                        setState((state) => {
                            state.data.draftPromoCode = draftPromoCode;
                        }),
                    setHasFreshData: (hasFreshData: boolean) =>
                        setState((state) => {
                            state.data.hasFreshData = hasFreshData;
                        }),
                    setCustomer: (customer: Customer) =>
                        setState((state) => {
                            state.data.me.customer = customer;
                        }),
                    setMyAddresses: (customer: Customer) =>
                        setState((state) => {
                            // This logic should be simplified to improve readability
                            if (!state.data.me.customer) state.data.me.customer = {} as Customer;
                            const { id, version, addresses, defaultShippingAddressId } =
                                state.data.me.customer ?? {};
                            if (id !== customer.id) {
                                state.data.me.customer.id = customer.id;
                                state.data.me.customer.version = customer.version;
                                state.data.me.customer.addresses = customer.addresses;
                                state.data.me.customer.defaultShippingAddressId =
                                    customer.defaultShippingAddressId;
                                return;
                            }
                            if (!version) {
                                state.data.me.customer.version = customer.version;
                            }
                            if (version < customer.version) {
                                // if equal no need to set, if greater better not set
                                state.data.me.customer.version = customer.version;
                            }
                            if (JSON.stringify(addresses) !== JSON.stringify(customer.addresses)) {
                                state.data.me.customer.addresses = customer.addresses;
                                state.data.me.customer.defaultShippingAddressId =
                                    customer.defaultShippingAddressId;
                                return;
                            }
                            if (!defaultShippingAddressId) {
                                return;
                            }
                            if (defaultShippingAddressId !== customer?.defaultShippingAddressId) {
                                state.data.me.customer.defaultShippingAddressId =
                                    customer.defaultShippingAddressId;
                            }
                        }),
                    getStoreData: () => get().data.storeData,
                    setStoreData: (newData: StoreData) =>
                        setState((state) => {
                            state.data.storeData = newData;
                        }),
                    setStores: (stores: StoreData[]) =>
                        setState((state) => {
                            state.data.stores = stores;
                        }),
                    getStores: () => get().data.stores,
                    getPolicyAccepted: () => get().data.policyAccepted,
                    setPolicyAccepted: (policyAccepted: boolean) =>
                        setState((state) => {
                            state.data.policyAccepted = policyAccepted;
                        }),
                    getParcelShopData: () => get().data.parcelShopData,
                    setParcelShopData: (newData: StoreData) =>
                        setState((state) => {
                            state.data.parcelShopData = newData;
                        }),
                    setMyProfile: (customer: Customer) => setState(updateProfile(customer)),
                    setUtmParams: (utmParams: Tracking["utm"]) =>
                        setState((state) => {
                            state.data.tracking.utm = utmParams;
                        }),
                    getUtmParams: () => get().data.tracking.utm,
                    getCart: () => get().data.me.cartInfo.cart,
                    getMasterProducts: () => get().data.me.cartInfo.masterProducts,
                    setSelectedVariant: (
                        selectedVariant: {
                            selectedVariant: ProductVariant;
                            isAvailable: boolean;
                        } | null
                    ) =>
                        setState((state) => {
                            state.data.pdpState.selectedVariant = selectedVariant;
                        }),
                    setIsOpenAddedToBagModal: (modalState: boolean) =>
                        setState((state) => {
                            state.data.pdpState.isOpenAddedToBagModal = modalState;
                        }),
                    setTempCustomerData: (firstName: string, lastName: string) =>
                        setState((state) => {
                            state.data.tempCustomerData.firstName = firstName;
                            state.data.tempCustomerData.lastName = lastName;
                        }),
                    getTempCustomerData: () => get().data.tempCustomerData,
                    clearTempCustomerData: () =>
                        setState((state) => {
                            state.data.tempCustomerData.firstName = null;
                            state.data.tempCustomerData.lastName = null;
                        }),
                    setClickAndCollectStores: (stores: StoreData[]) =>
                        setState((state) => {
                            state.data.pdpState.clickAndCollectStores = stores;
                        }),
                    setPagePathData: (pagePathData: pagePathData) =>
                        setState((state) => {
                            state.data.pagePathData = pagePathData;
                        }),
                    getPagePathData: () => get().data.pagePathData,
                    setPreviewMode: (isPreview: boolean) =>
                        set(
                            produce((state) => {
                                state.data.isPreviewMode = isPreview;
                            })
                        ),
                    getPreviewMode: () => get().data.isPreviewMode,
                    setGalleryOpen: (isOpen) =>
                        setState((state) => {
                            state.data.productImageGallery.isGalleryOpen = isOpen;
                        }),
                    setGalleryImageIndex: (index) =>
                        setState((state) => {
                            state.data.productImageGallery.galleryImageIndex = index;
                        }),
                    setHasActiveTimer: (hasActiveTimer) =>
                        setState((state) => {
                            state.data.hasActiveTimer = hasActiveTimer;
                        }),
                };
            },
            {
                partialize: (state) => ({
                    data: {
                        me: {
                            globalShoeSizeConvention: state.data.me.globalShoeSizeConvention,
                            customer: state.data.me.customer,
                        },
                        storeData: state.data.storeData,
                        parcelShopData: state.data.parcelShopData,
                        tempCustomerData: state.data.tempCustomerData,
                        pagePathData: state.data.pagePathData,
                    },
                }),
                name: "ecco-store",
                merge: (persistedState: Partial<StoreState>, currentState) => {
                    if (
                        !shoeConventionForSite.includes(
                            persistedState.data.me.globalShoeSizeConvention
                        )
                    ) {
                        return merge(currentState, {
                            data: {
                                me: {
                                    globalShoeSizeConvention: shoeConventionForSite[0],
                                },
                            },
                        });
                    } else {
                        return merge(currentState, persistedState);
                    }
                },
            }
        )
    );
};

const zustandContext = createContext<StoreApi<StoreState>>(initializeStore(initialState));

export const ZustandProvider = ({ children, initialState = {} }) => {
    const storeRef = useRef<StoreApi<StoreState>>();
    if (!storeRef.current) {
        storeRef.current = initializeStore(initialState);
    }

    return <zustandContext.Provider value={storeRef.current}>{children}</zustandContext.Provider>;
};

export function useStore<T>(selector: (state: StoreState) => T) {
    const store = useContext(zustandContext);

    if (!store) throw new Error("Store is missing the provider");

    return useZustandStore(store, selector) as T;
}

export const useStoreApi = () => {
    return useContext(zustandContext);
};

export function useCreateStore(initialState: { data: StoreDataType }) {
    // For CSR, always re-use same store.
    const store = initializeStore(initialState);
    // And if initialState changes, then merge states in the next render cycle.
    //
    // eslint complaining "React Hooks must be called in the exact same order in every component render"
    // is ignorable as this code runs in same order in a given environment
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useIsomorphicLayoutEffect(() => {
        if (initialState && store) {
            store.setState({
                ...store.getState(),
                ...initialState,
            });
        }
    }, [initialState]);

    return () => store;
}
